import { leavingToSignIn } from './signinPage.js'

// One multi-tenant app registration. 'organizations' accepts every work/school tenant; a
// tenant that wants personal Microsoft accounts needs BOTH .../common here and
// allow_personal_ms server-side, deliberately.
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID || ''
const authority =
  import.meta.env.VITE_ENTRA_AUTHORITY || 'https://login.microsoftonline.com/organizations'

export const msalEnabled = Boolean(clientId)

const SCOPES = ['openid', 'profile', 'email']

// MSAL is ~150 KB gzipped and is imported dynamically, on the first click of the sign-in
// button. A paired wall TV never signs in, and making it download an auth library it cannot
// use — on shop wifi, on every watchdog reload — would be a daily tax for nothing. A phone
// coming back from a full-page sign-in loads it too, to read Microsoft's answer.
let instance = null

async function getMsal() {
  if (!msalEnabled) throw new Error('Microsoft sign-in is not configured')
  if (!instance) {
    const { PublicClientApplication } = await import('@azure/msal-browser')
    instance = new PublicClientApplication({
      // The way back to where the person was is the app's own (lib/signinPage.js): left to
      // MSAL, a full-page sign-in would load the app a second time to get there.
      auth: { clientId, authority, redirectUri: window.location.origin, navigateToLoginRequestUrl: false },
      cache: { cacheLocation: 'sessionStorage', storeAuthStateInCookie: false },
    })
    await instance.initialize()
  }
  return instance
}

/** Returns the raw id_token; the server does every bit of the verification. */
export async function signInWithMicrosoft() {
  const msal = await getMsal()
  const result = await msal.loginPopup({ scopes: SCOPES })
  return result.idToken
}

/** Sends this page to Microsoft. It comes back to the app, where finishMicrosoftPage reads the answer. */
export async function leaveForMicrosoft() {
  const msal = await getMsal()
  // With no answer to read, this clears what an abandoned sign-in left behind (somebody
  // pressing Back on Microsoft's page). MSAL would otherwise refuse to start another.
  await msal.handleRedirectPromise()
  leavingToSignIn('microsoft')
  await msal.loginRedirect({ scopes: SCOPES })
}

/** The raw id_token Microsoft sent back to this page. */
export async function finishMicrosoftPage() {
  const msal = await getMsal()
  let result
  try {
    result = await msal.handleRedirectPromise()
  } catch (err) {
    // MSAL's own message for this is a line of AADSTS codes.
    if (err?.errorCode === 'access_denied' || err?.errorCode === 'user_cancelled') throw new Error('Sign-in was cancelled.')
    throw err
  }
  if (!result?.idToken) throw new Error('Sign-in did not complete — please try again')
  return result.idToken
}
